import { useState, useEffect } from 'react'
import { Rnd } from 'react-rnd'
import { pixelToPercent, percentToPixel } from 'utils/calculatorHelpers'
import { ETAGE_MAX_WIDTH } from 'components/Resize/Resize'
import classNames from 'classnames'
import Etage from 'interfaces/Etage'
import Form from 'react-bootstrap/Form'
import styles from './Etage.module.scss'

interface Props {
  etage: Etage
  bounds: string
  className?: string
  onResize: (newEtage: Etage) => void
  onResizeStop: () => void
  resizeHandleComponent?: {
    left: JSX.Element
    right: JSX.Element
  }
}

const HEIGHT = 60
const defaultProps = {
  className: undefined,
  resizeHandleComponent: {},
}

const MIN_WIDTH_PERCENTAGE = 15

const ResizableEtage = ({
  etage,
  bounds,
  className,
  onResize,
  onResizeStop,
  resizeHandleComponent,
}: Props): JSX.Element => {
  const [inputWidth, setInputWidth] = useState(etage.width)

  const updateWidth = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // 15% < updatedWidth < 100%
    let updatedWidth = inputWidth
    if (updatedWidth < MIN_WIDTH_PERCENTAGE) updatedWidth = MIN_WIDTH_PERCENTAGE
    if (updatedWidth > 100) updatedWidth = 100

    // Compute if position needs to change.
    // Ex:
    // x1 = position.x = 50
    // x2 = width = 20% = 50
    // input = 100% = 250
    // then x1 needs to be 0 or etage won't fit
    // <------------- Xmax -------------->
    //           <-- x2 --->
    // <-- x1 -->[__etage__]<-- delta --->
    // |_________________________________|

    let x1 = etage.position.x
    const x2 = etage.width
    const input = percentToPixel(updatedWidth)
    const xMax = ETAGE_MAX_WIDTH
    const delta = xMax - (x1 + x2)
    if (input > delta + x2) {
      x1 = xMax - input
    }

    onResize({
      width: updatedWidth,
      position: { ...etage.position, x: x1 },
    })
  }

  const onWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = Number.parseInt(event.target.value, 10)
    setInputWidth(newWidth)
  }

  useEffect(() => {
    setInputWidth(etage.width)
  }, [etage.width])

  return (
    <div className={styles.floor}>
      <Rnd
        className={classNames(styles.rnd, className)}
        disableDragging
        bounds={bounds}
        enableResizing={{
          bottom: false,
          bottomLeft: false,
          bottomRight: false,
          left: true,
          right: true,
          top: false,
          topLeft: false,
          topRight: false,
        }}
        minWidth={ETAGE_MAX_WIDTH * (MIN_WIDTH_PERCENTAGE / 100)}
        size={{
          width: percentToPixel(etage.width),
          height: HEIGHT,
        }}
        position={etage.position}
        onResize={(_1, _2, ref, _4, position) =>
          onResize({ width: pixelToPercent(ref.offsetWidth), position })
        }
        onResizeStop={onResizeStop}
        resizeHandleComponent={resizeHandleComponent}
      >
        <Form
          className={styles.inputForm}
          onSubmit={updateWidth}
          onBlur={updateWidth}
        >
          <Form.Control
            className={classNames(styles.inputWidth, {
              [styles.hideUnit]: etage.width < 21,
            })}
            onFocus={(event) => event.target.select()}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputWidth || ''}
            onChange={onWidthChange}
            min={0}
            max={100}
          />
          <span className={styles.unit}>%</span>
        </Form>
      </Rnd>
    </div>
  )
}
ResizableEtage.defaultProps = defaultProps

export default ResizableEtage
